import React from 'react';
import { Sparkles, Edit2, Archive, ArchiveRestore, Share2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = DOMPurify.sanitize(html);
  tmp.querySelectorAll('p, h1, h2, h3, h4, li, div, br').forEach(el => {
    el.insertAdjacentText('beforebegin', ' ');
  });
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

const STATUS_CONFIG = {
  idea:        { label: 'Idea',        color: 'bg-[var(--text-muted)]', text: 'text-white' },
  in_progress: { label: 'In Progress', color: 'bg-[var(--secondary)]',  text: 'text-white' },
  done:        { label: 'Done',        color: 'bg-[#30D158]',           text: 'text-white' },
};

const IdeaCard = ({ idea, enhancingId, onEnhance, onEdit, onToggleArchive, onDelete, onStatusChange }) => {
  const handleShare = () => {
    const text = `${idea.title}\n\n${idea.content.replace(/<[^>]*>/g, '')}`;
    if (navigator.share) {
      navigator.share({ title: idea.title, text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Idea copied to clipboard!');
    }
  };

  const status = idea.status || 'idea';
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.idea;
  const isDone = status === 'done';

  return (
    <div
      data-testid={`idea-card-${idea._id}`}
      className={`bg-[var(--surface)] border-2 border-[var(--border-default)] p-4 sm:p-6 flex flex-col gap-3 sm:gap-4 relative idea-card corner-accent ${isDone ? 'opacity-60' : ''}`}
    >
      <div className={`absolute top-0 left-0 right-0 h-1 ${isDone ? 'bg-[#30D158]' : 'bg-gradient-to-r from-[#FF3B30] via-[#007AFF] to-[#FFD60A]'}`} />
      
      {/* Status + Category badges */}
      <div className="absolute top-4 sm:top-5 right-3 sm:right-4 flex items-center gap-2">
        <select
          data-testid={`status-select-${idea._id}`}
          value={status}
          onChange={(e) => onStatusChange?.(idea._id, e.target.value)}
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-[var(--border-default)] cursor-pointer appearance-none ${cfg.color} ${cfg.text}`}
          style={{ backgroundImage: 'none', paddingRight: '8px' }}
        >
          <option value="idea">Idea</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
        <span className="category-badge-dark text-xs font-mono uppercase px-2.5 py-1.5 border border-[var(--border-default)] inline-block">
          {idea.category}
        </span>
      </div>

      <h3
        className={`font-bold text-lg sm:text-xl tracking-tight line-clamp-2 pr-16 sm:pr-48 pt-1 ${isDone ? 'line-through decoration-[var(--text-muted)]' : ''}`}
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {idea.title}
      </h3>

      <div className="text-sm text-[var(--text-secondary)] line-clamp-3 leading-relaxed">
        {stripHtml(idea.content) || 'No content'}
      </div>

      {idea.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {idea.tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 border border-[var(--border-default)] bg-[var(--surface-alt)] text-xs font-mono uppercase tracking-wider tag-hover transition-colors duration-150"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-auto pt-3 sm:pt-4 border-t-2 border-[var(--border-subtle)]">
        <button
          data-testid={`enhance-btn-${idea._id}`}
          onClick={() => onEnhance(idea)}
          disabled={enhancingId === idea._id}
          className={`flex-1 min-w-[100px] bg-[var(--secondary)] text-white font-bold px-2 sm:px-3 py-2 border-2 border-[var(--border-default)] flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm transition-all duration-150 hover:opacity-90 btn-shine ${
            enhancingId === idea._id ? 'ai-enhancing cursor-wait' : ''
          }`}
        >
          <Sparkles size={14} />
          <span className="hidden sm:inline">{enhancingId === idea._id ? 'Enhancing...' : 'AI Enhance'}</span>
          <span className="sm:hidden">AI</span>
        </button>
        <button
          data-testid={`edit-btn-${idea._id}`}
          onClick={() => onEdit(idea)}
          className="bg-transparent action-btn-hover px-2 sm:px-3 py-2 transition-colors duration-150 border-2 border-[var(--border-default)]"
        >
          <Edit2 size={16} />
        </button>
        <button
          data-testid={`archive-btn-${idea._id}`}
          onClick={() => onToggleArchive(idea._id)}
          className="bg-transparent action-btn-hover px-2 sm:px-3 py-2 transition-colors duration-150 border-2 border-[var(--border-default)]"
        >
          {idea.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
        </button>
        <button
          data-testid={`share-btn-${idea._id}`}
          onClick={handleShare}
          className="bg-transparent action-btn-hover px-2 sm:px-3 py-2 transition-colors duration-150 border-2 border-[var(--border-default)]"
        >
          <Share2 size={16} />
        </button>
        <button
          data-testid={`delete-btn-${idea._id}`}
          onClick={() => onDelete(idea._id)}
          className="bg-transparent text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white px-2 sm:px-3 py-2 transition-colors duration-150 border-2 border-[var(--border-default)]"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default IdeaCard;
